import { EVENT } from "@/constants/event";
import { isSafari } from "@/utils/isSafari";
import { useEventListener, useMemoizedFn, usePrevious } from "ahooks";
import classNames from "classnames";
import { debounce } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";
import { useDraggable } from "./useDraggable";

export default forwardRef(
  /**
   * 瀑布流
   * @param {*} props
   */
  function Masonry(
    {
      brickId = "id",
      bricks = [],
      render,
      gutter = 16,
      columnSize = 240,
      columnNum = 4,
      children,
      threshold = 1, // 预加载的滚动区域，小于10是容器倍数，大于10是绝对值
      scrollElement, // 滚动元素
      className = "masonry",
      brickClassName = "brick",
      masonryKey = "masonryKey", // 切换不同的瀑布流实例，用于，重新计算 containerOffsetTop 值
      onRendered = () => {},
      loadingContent = null,
      // getBrickHeight,
      onDragSortEnd,
      enableDragSort,
      asyncGetContainerOffsetTop = false,
      hasMore,
      // 手动控制是否可以进行布局，用于解决卡片会变化需要重新布局的场景
      canLayout = true,
    },
    ref
  ) {
    const containerRef = useRef(null);
    const computedBricks = useRef(new Map()); // 已经计算大小和位置的bricks
    const columnHeightArr = useRef([]); // 瀑布流各列高度
    const containerScrollHeight = useRef(0);
    const [containerOffsetTop, setContainerOffsetTop] = useState(0); // 瀑布流容器位置
    const [containerHeight, setContainerHeight] = useState(0); // 瀑布流容器高度
    const [scrollTop, setScrollTop] = useState(0); // 页面滚动高度
    const isUnmount = useRef(false); // 是否销毁
    const loadingRef = useRef(true);
    const prevBricks = usePrevious(bricks);

    useMemo(() => {
      if (prevBricks?.length > bricks.length) {
        // 当瀑布流重置时，需要重置高度缓存
        containerScrollHeight.current = 0;
      }
    }, [bricks, prevBricks]);

    useEffect(() => {
      isUnmount.current = false;
      return () => {
        isUnmount.current = true;
      };
    }, []);

    const { isDragging, handleDragStart } = useDraggable({ onDragSortEnd });

    const renderChild = useMemo(() => {
      const childrenNode = React.Children.toArray(children);
      return function Fn(record, index) {
        const computedBrickMap = computedBricks.current;

        let props = {
          className: styles.brick,
          draggable: false,
        };

        if (enableDragSort && !record?.disableDrag) {
          props.onMouseDown = (e) => handleDragStart(e, record);
          props["data-mark"] = record[brickId];
        }

        if (computedBrickMap.has(record[brickId])) {
          const rect = computedBrickMap.get(record[brickId]);
          props = {
            ...props,
            className: classNames(styles.brick, styles.show, brickClassName),
            style: { transform: `translate(${rect.left}px, ${rect.top}px)` },
          };
        }

        // 先渲染 组件子元素
        if (record.mansonryInnerChildIndex !== undefined) {
          return (
            <div key={record.mansonryInnerChildIndex} {...props}>
              {childrenNode[record.mansonryInnerChildIndex]}
            </div>
          );
        }

        return (
          <div key={record[brickId]} {...props}>
            {render(record, index)}
          </div>
        );
      };
    }, [
      brickId,
      children,
      render,
      brickClassName,
      handleDragStart,
      enableDragSort,
    ]);

    // 获取瀑布流容器位置
    useEffect(() => {
      const getContainerOffsetTop = () => {
        const realScrollElement =
          typeof scrollElement === "string"
            ? document.getElementById(scrollElement)
            : scrollElement || document.documentElement;

        const containerRect = containerRef.current?.getBoundingClientRect();
        const containerRectTop = containerRect?.top;

        if (!scrollElement) {
          setContainerOffsetTop(containerRectTop + window.scrollY);
        } else {
          setContainerOffsetTop(
            containerRectTop -
              realScrollElement?.getBoundingClientRect()?.top +
              realScrollElement.scrollTop
          );
        }
      };

      // 立即执行一次
      getContainerOffsetTop();

      // Safari浏览器问题，没有延迟获取，他会读取一个错误的异常值，需要异步再读取一下
      if (isSafari() || asyncGetContainerOffsetTop) {
        let rafId = requestAnimationFrame(getContainerOffsetTop);
        return () => {
          cancelAnimationFrame(rafId);
        };
      }
    }, [scrollElement, masonryKey, asyncGetContainerOffsetTop]);

    // 获取瀑布流容器高度
    const getContainerHeight = useCallback(() => {
      // 真实滚动元素
      const realScrollElement =
        typeof scrollElement === "string"
          ? document.getElementById(scrollElement)
          : scrollElement || document.documentElement;
      const containerHeight = realScrollElement?.clientHeight || 0;
      setContainerHeight(containerHeight);
    }, [scrollElement]);

    useEffect(() => {
      getContainerHeight();
      window.addEventListener("resize", getContainerHeight);
      return () => {
        window.removeEventListener("resize", getContainerHeight);
      };
    }, [getContainerHeight]);

    // 所有要渲染的元素：子元素+列表
    const allBricks = useMemo(() => {
      let list = [];
      React.Children.map(children, (child, index) => {
        if (child) {
          list.push({
            mansonryInnerChildIndex: index,
            [brickId]: `childId_${index}`,
          });
        }
      });
      list.push(...bricks);
      return list;
    }, [brickId, bricks, children]);

    // 顶部加载新数据 或 删除某个brick
    useEffect(() => {
      const bricks = allBricks;

      // 清除bricks
      if (!bricks.length) {
        columnHeightArr.current.fill(0);
        computedBricks.current.clear();
        return;
      }

      const keys = {};
      let index = 0;
      for (let [key] of computedBricks.current) {
        let realKey = bricks[index] && bricks[index][brickId];

        // 循环跳过重复的brick
        while (keys[realKey] && index < bricks.length - 1) {
          index += 1;
          realKey = bricks[index][brickId];
        }

        if (!bricks[index] || bricks[index][brickId] !== key) {
          // 变更出现在前面几个，直接全部清空
          if (index < 5) {
            columnHeightArr.current.fill(0);
            computedBricks.current.clear();
            break;
          }

          // 清除当前brick后的所有brick缓存
          let clearAfter = false;
          for (let [remainKey, remainBrick] of computedBricks.current) {
            if (clearAfter || remainKey === key) {
              clearAfter = true;

              computedBricks.current.delete(remainKey);
              columnHeightArr.current[remainBrick.column] = Math.min(
                remainBrick.top,
                columnHeightArr.current[remainBrick.column]
              );
            }
          }
          break;
        }

        keys[key] = true;
        index += 1;
      }
    }, [allBricks, brickId]);

    const recompute = useMemoizedFn(() => {
      // 重排后每列高度会变化，需要清空
      columnHeightArr.current = Array(columnNum).fill(0);
      const columnHeightArrCurrent = columnHeightArr.current;
      const computedBrickMap = computedBricks.current;
      const maxColumn = columnNum;

      const newMap = new Map();

      // 遍历allBricks,根据缓存的内容，重新建立高度
      for (let index = 0; index < allBricks.length; index++) {
        const key = allBricks[index]?.[brickId];
        const rect = computedBrickMap.get(key);
        if (rect) {
          let minHeight = Infinity;
          let columNum = 0;
          // 获取最小的列
          columnHeightArrCurrent.forEach((value, i) => {
            if (value < minHeight) {
              columNum = i;
              minHeight = value;
            }
          });

          const clientHeight = rect.height;
          const newRect = {
            column: columNum,
            left: columNum * (columnSize + gutter),
            top: minHeight,
            height: clientHeight,
            bottom: minHeight + clientHeight,
            right: columNum * (columnSize + gutter) + columnSize,
          };

          // 更新已经渲染数据
          newMap.set(key, newRect);
          columnHeightArrCurrent[columNum] =
            minHeight +
            (clientHeight > 0 // clientHeight没有高度时，不增加 gutter 高度
              ? clientHeight + gutter
              : 0);

          if (allBricks[index]?.mergeCol) {
            // 给横跨的列都添加高度
            for (let i = 1; i < allBricks[index]?.mergeCol; i++) {
              // 不超过最大列数
              if (columNum + i < maxColumn) {
                columnHeightArrCurrent[columNum + i] +=
                  clientHeight > 0 ? clientHeight + gutter : 0;
              }
            }
          }
        }
      }

      computedBricks.current = newMap;
    });

    // 重新渲染整个瀑布流所有内容
    const relayout = useCallback(
      (isReCompute) => {
        if (typeof window === "undefined") {
          return;
        }

        columnHeightArr.current = Array(columnNum).fill(0);
        if (isReCompute) {
          // 元素高度没有变化，只是重排，秩序重新计算布局即可
          recompute();
        } else {
          // 元素高度有变化，清空布局
          computedBricks.current.clear();
          containerScrollHeight.current = 0;
        }

        // @TODO: 临时解决列数变化情况，触发滚动，重新布局
        setScrollTop((scrollTop) => scrollTop + 1);
      },
      [columnNum, recompute]
    );

    // 列数量、尺寸发生变化，需要同步执行relayout，不能异步（useEffect），不然会导致计算延迟而混乱
    useMemo(() => {
      relayout();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relayout, columnSize]);

    useEventListener(EVENT.MASONRY_RELAYOUT, (event) => {
      relayout(event.detail?.isReCompute);
    });

    // 可视区域坐标
    const visibleRect = useMemo(() => {
      const expandSize =
        threshold > 10 ? threshold : containerHeight * threshold;
      const top = scrollTop - containerOffsetTop - expandSize;

      const bottom =
        scrollTop + containerHeight - containerOffsetTop + expandSize;

      return { top, bottom };
    }, [threshold, containerHeight, containerOffsetTop, scrollTop]);

    // 需要被渲染的brick
    const renderBricks = useMemo(() => {
      // 服务端渲染
      if (typeof window === "undefined") {
        return allBricks;
      }

      const bricks = allBricks;
      const keys = {}; // 避免重复
      const beRenders = [];

      let unRect = 0; // 未定位过的元素个数
      for (let i = 0; i < bricks.length; i++) {
        const brick = bricks[i];
        const id = brick[brickId];
        const rect = computedBricks.current.get(id);
        if (rect) {
          if (rect.bottom > visibleRect.top && rect.top < visibleRect.bottom) {
            // 视口范围内
            if (!keys[id]) {
              beRenders.push(brick);
              keys[id] = true;
            }
          }
        } else {
          // 找到没有加载过的brick
          if (!keys[id]) {
            beRenders.push(brick);
            keys[id] = true;
            unRect += 1;
          }
        }
      }

      return beRenders;
    }, [allBricks, brickId, visibleRect]);

    const runFlagRef = useRef(false);
    // 定位所有Dom节点
    useEffect(() => {
      function positionBrick() {
        if (!canLayout) {
          return;
        }

        const container = containerRef.current;

        // safari浏览器渲染问题，会导致containerRef.current为null，所以需要判断一下
        if (!container) {
          return;
        }

        const maxColumn = columnNum;

        const brickDom = [...(container.children || [])];

        const computedBrickMap = computedBricks.current;
        const columnHeightArrCurrent = columnHeightArr.current;
        let unRect = 0;
        let outVisibleRect = false;
        brickDom.forEach((brick, index) => {
          // 忽略最后一个loading子节点
          if (brick.id === "loading" || !renderBricks[index]) {
            return;
          }

          const rect = computedBrickMap.get(renderBricks[index]?.[brickId]);

          if (!rect) {
            let columNum = 0;
            let minHeight = Infinity;

            // 新的brick
            columnHeightArrCurrent.forEach((value, i) => {
              if (value < minHeight) {
                columNum = i;
                minHeight = value;
              }
            });

            const clientHeight = Math.floor(brick.clientHeight);
            // const clientWidth = columnSize;
            const rect = {
              column: columNum,
              left: columNum * (columnSize + gutter),
              top: minHeight,
              height: clientHeight,
              bottom: minHeight + clientHeight,
              right: columNum * (columnSize + gutter) + columnSize,
            };

            if (
              (rect.bottom > visibleRect.top &&
                rect.top < visibleRect.bottom) ||
              !visibleRect.top
            ) {
              // 视口范围内
              Promise.resolve().then(() => {
                brick.classList.add(styles.show);
                brick.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
              });
            }

            // 更新已经渲染数据
            computedBrickMap.set(renderBricks[index]?.[brickId], rect);

            // 更新已计算的高度
            columnHeightArrCurrent[columNum] =
              minHeight +
              (clientHeight > 0 // clientHeight没有高度时，不增加 gutter 高度
                ? clientHeight + gutter
                : 0);

            if (renderBricks[index]?.mergeCol) {
              // 给横跨的列都添加高度
              for (let i = 1; i < renderBricks[index]?.mergeCol; i++) {
                // 不超过最大列数
                if (columNum + i < maxColumn) {
                  columnHeightArrCurrent[columNum + i] +=
                    clientHeight > 0 ? clientHeight + gutter : 0;
                }
              }
            }

            unRect += 1;
            if (rect.top > visibleRect.bottom) {
              outVisibleRect = true;
            }
          }
        });

        containerScrollHeight.current = Math.max(
          ...columnHeightArr.current,
          containerScrollHeight.current
        );
        container.style.height = containerScrollHeight.current + "px";

        // 加载更多未定位brick
        if (unRect >= 20 && !outVisibleRect) {
          setScrollTop((scrollTop) => scrollTop + 1);
        }

        // 定位完成后回调函数
        onRendered();

        if (renderBricks.length > 0 || !hasMore) {
          loadingRef.current = false;
        }
      }

      // Safari浏览器渲染问题，Safari会在css没有应用时，就执行这里的代码，导致获取dom的宽高尺寸等信息不对，继而导致瀑布流展示异常，所以需要通过异步解决这个问题
      // 通过runFlagRef控制，只有第一次执行时需要异步，后续执行时直接执行
      if (isSafari() && !runFlagRef.current) {
        runFlagRef.current = true;
        requestAnimationFrame(positionBrick);
      } else {
        positionBrick();
      }
    }, [
      renderBricks,
      visibleRect,
      columnSize,
      gutter,
      brickId,
      onRendered,
      columnNum,
      hasMore,
      canLayout,
    ]);

    // 滚动事件
    useEffect(() => {
      // 没有提供 scrollElement 的话，绑定window的滚动事件
      let target =
        typeof scrollElement === "string"
          ? document.getElementById(scrollElement)
          : scrollElement || window;

      const change = debounce(
        () => {
          if (isUnmount.current) {
            return;
          }

          // 获取真实 DOM是scrollTop， window是scrollY
          setScrollTop(target.scrollTop || target.scrollY || 0);
        },
        100,
        { trailing: true, maxWait: 200, leading: false }
      );

      // 初始化设置
      change();

      // 绑定滚动事件
      target.addEventListener("scroll", change, { passive: true });

      // 取消滚动事件
      return () => {
        target.removeEventListener("scroll", change, { passive: true });
      };
    }, [scrollElement]);

    useImperativeHandle(ref, () => ({
      // 获取所有图片的位置，用来做框选操作
      getBricksPosition: () => {
        return {
          containerOffsetTop:
            containerRef.current?.getBoundingClientRect()?.top,
          containerOffsetLeft:
            containerRef.current?.getBoundingClientRect()?.left,
          computedBricks,
        };
      },
      relayout,
    }));

    return (
      <div
        className={classNames([
          styles.masonry,
          className,
          isDragging && styles.isDragging,
        ])}
        style={{
          gridAutoColumns: `${columnSize}px`,
          gap: `${gutter}px`,
        }}
        ref={containerRef}
      >
        {/* {console.count("Masonry Render")} */}
        {renderBricks.map(renderChild)}
        {loadingRef.current && (
          <div id="loading" className={styles.loading}>
            {loadingContent}
          </div>
        )}
      </div>
    );
  }
);
